import React, { useEffect, useMemo, useState } from 'react';
import { Icons } from '../icons/Icons';
import { isGoogleDriveConfigured, listDriveFolders } from '../../services/googleDrive';
import { megaProvider } from '../../integrations/providers/megaProvider';
import { useI18n } from '../../context/I18nContext';

const ROOT_FOLDERS = {
  google_drive: { id: 'root', name: 'Il mio Drive', path: '' },
  mega: { id: 'mega-root', name: 'MEGA', path: '' }
};

const CloudFolderPickerModal = ({ providerId, onClose, onSelect }) => {
  const { t, language } = useI18n();
  const providerName = providerId === 'mega' ? 'MEGA' : 'Google Drive';
  const rootFolder = providerId === 'google_drive'
    ? { ...ROOT_FOLDERS.google_drive, name: t('cloudFolderPicker.googleDriveRoot') }
    : (ROOT_FOLDERS[providerId] || ROOT_FOLDERS.google_drive);
  const [currentFolder, setCurrentFolder] = useState(rootFolder);
  const [breadcrumbs, setBreadcrumbs] = useState([rootFolder]);
  const [folders, setFolders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [megaCredentials, setMegaCredentials] = useState({
    email: '',
    password: '',
    secondFactorCode: ''
  });
  const [megaConnected, setMegaConnected] = useState(false);

  const filteredFolders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return folders;
    return folders.filter(folder => folder.name.toLowerCase().includes(query));
  }, [folders, searchTerm]);

  const loadGoogleFolders = async (folder, options = {}) => {
    if (!isGoogleDriveConfigured()) {
      throw new Error(t('cloudFolderPicker.googleDriveNotConfigured'));
    }

    return listDriveFolders(folder.id, options);
  };

  const loadMegaFolders = async (folder, options = {}) => {
    if (!megaConnected) {
      if (!megaCredentials.email || !megaCredentials.password) {
        throw new Error(t('cloudFolderPicker.megaCredentialsRequired'));
      }

      await megaProvider.connect(megaCredentials);
      setMegaConnected(true);
    }

    return megaProvider.listFolders({ folderPath: folder.path }, options);
  };

  const loadFolders = async (folder = currentFolder, options = {}) => {
    setError('');
    setIsLoading(true);

    try {
      const nextFolders = providerId === 'mega'
        ? await loadMegaFolders(folder, options)
        : await loadGoogleFolders(folder, options);

      setFolders(nextFolders);
    } catch (loadError) {
      setError(loadError.message || t('cloudFolderPicker.connectionError', { provider: providerName }));
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (providerId !== 'mega') {
      loadFolders(rootFolder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, language]);

  const openFolder = async (folder) => {
    setCurrentFolder(folder);
    setBreadcrumbs(prev => [...prev, folder]);
    setSearchTerm('');
    await loadFolders(folder);
  };

  const openBreadcrumb = async (folder, index) => {
    setCurrentFolder(folder);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
    setSearchTerm('');
    await loadFolders(folder);
  };

  const handleMegaCredentialChange = (field, value) => {
    setMegaCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelect = () => {
    onSelect({
      id: currentFolder.id,
      name: currentFolder.name,
      path: currentFolder.path || ''
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-3xl max-h-[88vh] rounded-lg shadow-2xl overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-gray-950 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{t('cloudFolderPicker.chooseFolderTitle', { provider: providerName })}</h2>
            <p className="text-sm text-gray-300">{t('cloudFolderPicker.chooseFolderDesc')}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10" aria-label={t('common.close')}>
            <Icons.X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-hidden flex flex-col">
          {providerId === 'mega' && !megaConnected && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="email"
                  value={megaCredentials.email}
                  onChange={(event) => handleMegaCredentialChange('email', event.target.value)}
                  placeholder={t('cloudFolderPicker.megaEmail')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="password"
                  value={megaCredentials.password}
                  onChange={(event) => handleMegaCredentialChange('password', event.target.value)}
                  placeholder={t('cloudFolderPicker.megaPassword')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={megaCredentials.secondFactorCode}
                  onChange={(event) => handleMegaCredentialChange('secondFactorCode', event.target.value)}
                  placeholder={t('cloudFolderPicker.mega2fa')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={() => loadFolders(currentFolder)}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
              >
                {isLoading ? t('cloudFolderPicker.connecting') : t('cloudFolderPicker.connectMega')}
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-sm">
            {breadcrumbs.map((folder, index) => (
              <React.Fragment key={`${folder.id}:${folder.path}`}>
                {index > 0 && <span className="text-gray-400">/</span>}
                <button
                  onClick={() => openBreadcrumb(folder, index)}
                  className="font-medium text-blue-700 hover:text-blue-900"
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('cloudFolderPicker.searchFolder')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => loadFolders(currentFolder, { forceRefresh: true })}
              disabled={isLoading || (providerId === 'mega' && !megaConnected && (!megaCredentials.email || !megaCredentials.password))}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('cloudFolderPicker.refresh')}
            </button>
          </div>

          {error && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden flex-1 min-h-[260px]">
            {isLoading ? (
              <div className="h-full min-h-[260px] flex items-center justify-center text-gray-500">
                {t('cloudFolderPicker.loadingFolders')}
              </div>
            ) : filteredFolders.length === 0 ? (
              <div className="h-full min-h-[260px] flex items-center justify-center text-gray-500">
                {t('cloudFolderPicker.noFoldersFound')}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[42vh] overflow-y-auto">
                {filteredFolders.map(folder => (
                  <button
                    key={`${folder.id}:${folder.path || ''}`}
                    onClick={() => openFolder(folder)}
                    className="w-full px-4 py-3 flex items-center justify-between gap-4 text-left hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900 truncate">{folder.name}</span>
                    <span className="text-sm text-blue-700">{t('cloudFolderPicker.open')}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="text-sm text-gray-600 min-w-0">
              Cartella selezionata: <span className="font-semibold text-gray-900">{currentFolder.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleSelect}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700"
              >
                Usa questa cartella
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudFolderPickerModal;
